/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-11 22:41:00
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Layout, Card, Button } from '@shopify/polaris';
import { getFormValues, reduxForm, FieldArray, reset, change } from 'redux-form';
import { withI18n } from '@shopify/react-i18n';

import { routes } from 'src/routes';

import { isEmptySynonyms, checkValidateSynonyms, buildSynonyms } from 'src/helpers/synonym-helpers';
import InputError from 'src/components/views/synonym/input-error';
import SynonymOnCreate from 'src/components/views/synonym/synonyms-on-create';
import ActionsGroupOnCreating from 'src/components/views/synonym/actions-group-on-creating';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import ProcessAddon from 'src/components/views/subscription-v3/addon/process-addon';
import ProcessAddonV4 from 'src/components/views/subscription-v4/addon/process-addon';
import ToastWithAction from 'src/components/views/common/toast-with-action';

import { confirmLeavePage, getShop } from 'src/helpers';
import * as synonymActions from 'src/stores/actions/synonym-actions';
import * as quickStartGuideAction from 'actions/dashboard/quick-start-guide-action';

import type { ComposedProps, State } from './new.type';
import { StandardSearchTab } from 'src/constants/search';
import history from 'src/routes/history';
import {
  ADDONS_INFO,
  ADDONS_INFO_V4,
  AI_POWERED_SEARCH_NAME,
  SUBSCRIPTION_STATE
} from 'src/constants/subscription';
import { getAddonStatus } from 'src/stores/actions/subscription-v3-actions';
import { getAISearchStatus } from 'src/helpers/search-settings-helper';
import { SynonymType } from 'src/constants/synonym';

class NewSynonym extends Component<ComposedProps, State> {
  state = {
    dismissedBanner: true,
    messageError: '',
    modalAddon: false,
    toastOptions: {
      message: '',
      actionContent: ''
    }
  };

  componentDidMount() {
    try {
      const { dispatch, initialValues, isSubscribedApp, getAISynonyms } = this.props;
      const synonyms = _.get(initialValues, 'synonyms', []);
      const searchTerm = localStorage.getItem(`${getShop()}-synonym-search-term`);
      if (searchTerm) {
        dispatch(
          change(
            'NewSynonymForm',
            'synonyms',
            synonyms.map((item, index) => {
              if (index === 0) {
                return {
                  ...item,
                  searchTerm
                };
              }
              return item;
            })
          )
        );
        localStorage.removeItem(`${getShop()}-synonym-search-term`);
      }

      if (isSubscribedApp) getAISynonyms(0, 10);
    } catch (error) {}

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillUnmount() {
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
  }

  render() {
    const {
      i18n,
      dirty,
      handleSubmit,
      pristine,
      submitting,
      saving,
      aiSearchStatus,
      isSubscribedApp,
      aiSynonyms,
      enabledSubscriptionV4,
      enableAISearch,
      formValues = {}
    } = this.props;
    const { dismissedBanner, messageError, modalAddon, toastOptions } = this.state;
    const emptySynonyms = formValues ? isEmptySynonyms(formValues['synonyms']) : true;

    const isSubscribeNow = aiSearchStatus !== SUBSCRIPTION_STATE.NOT_TRIAL;

    return (
      <div className="BC-Page BC-Page__Synonym">
        <Page
          title={i18n.translate('AdminHTML.Synonym.Add.title')}
          breadcrumbs={[
            {
              content: i18n.translate('AdminHTML.Synonym.Synonym.title'),
              onAction: () => this.backToManagementPage()
            }
          ]}
          primaryAction={
            <div className="BC-Page BC-Page__Button BC-Page__Button-HasBreadcrumb">
              <ActionsGroupOnCreating
                handleSubmit={handleSubmit(this.handleSave.bind(this))}
                discardChange={() => this.backToManagementPage()}
                pristine={pristine}
                submitting={submitting}
                saving={saving}
                emptySynonyms={emptySynonyms}
              />
            </div>
          }>
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />

                {/* Banner error */}
                {!dismissedBanner && (
                  <InputError onDismiss={this.handleDismissBanner} message={messageError} />
                )}
              </div>
            </Layout.Section>
            {/* List of synonym */}
            <Layout.Section>
              <Card sectioned>
                <Card.Section>
                  <form onSubmit={this.handleDefaultSubmit} className="newSynonyms">
                    <FieldArray
                      name="synonyms"
                      component={SynonymOnCreate}
                      getAISynonymSuggest={this.getAISynonymSuggest}
                      isSubscribedApp={isSubscribedApp && aiSynonyms.length > 0}
                    />
                  </form>
                </Card.Section>

                <Card.Section>
                  <div className="addMoreSynonyms">
                    <Button primary onClick={this.insertNewRow}>
                      {i18n.translate('AdminHTML.Common.Action.addSymbol')}
                    </Button>
                  </div>
                </Card.Section>
              </Card>

              <div className="pagination">
                <div className="actionsGroup">
                  <ActionsGroupOnCreating
                    handleSubmit={handleSubmit(this.handleSave.bind(this))}
                    discardChange={this.backToManagementPage}
                    pristine={pristine}
                    submitting={submitting}
                    saving={saving}
                    emptySynonyms={emptySynonyms}
                  />
                </div>
              </div>
            </Layout.Section>
          </Layout>
        </Page>

        {/* Confirm before leaving */}
        <RouteLeavingGuard
          when={dirty}
          // Navigate function
          navigate={path =>
            history.push({
              pathname: path,
              hash: StandardSearchTab.manualSynonyms
            })
          }
          shouldBlockNavigation={dirty}
        />

        {enabledSubscriptionV4 ? (
          <ProcessAddonV4
            openModalReview={modalAddon}
            addon={ADDONS_INFO_V4.find(e => e.addonName === AI_POWERED_SEARCH_NAME)}
            hadSubscribed={isSubscribeNow}
            handleAfterSubscribeAddon={() => getAddonStatus(AI_POWERED_SEARCH_NAME)}
            handleReviewModal={() => this.setState({ modalAddon: !modalAddon })}
          />
        ) : (
          <ProcessAddon
            openModalReview={modalAddon}
            addon={ADDONS_INFO.find(e => e.addonName === AI_POWERED_SEARCH_NAME)}
            hadSubscribed={isSubscribeNow}
            handleAfterSubscribeAddon={() => getAddonStatus(AI_POWERED_SEARCH_NAME)}
            handleReviewModal={() => this.setState({ modalAddon: !modalAddon })}
          />
        )}

        <ToastWithAction
          options={toastOptions}
          onAction={
            enableAISearch
              ? () => history.push(routes.aiSynonym.view())
              : () => {
                  this.setState({ modalAddon: true });
                }
          }
          onDismiss={() => {
            this.setState({ toastOptions: {} });
          }}
        />
      </div>
    );
  }

  // Back to management synonyms
  backToManagementPage = () => {
    history.push(routes.synonym.view());
  };

  // Insert new row
  insertNewRow = () => {
    const { formValues, updateField } = this.props;
    let { synonyms = [] } = formValues;
    // Create new object different from the old value in form,
    // to make redux form know the change: pristine = false
    let synonymsClone = _.cloneDeep(synonyms);

    synonymsClone.push({
      searchTerm: '',
      biDirection: '<=>',
      synonyms: '',
      allowSuggestion: false
    });

    // Update synonym form
    updateField('synonyms', synonymsClone);
  };

  getAISynonymSuggest = index => {
    const { i18n, formValues, updateField, enableAISearch, aiSynonyms } = this.props;

    try {
      // If use AI search => show toast to redirect to AI synonyms page
      if (enableAISearch) {
        this.setState({
          toastOptions: {
            message: i18n.translate('AdminHTML.Synonym.Validate.invalid_Synonyms_AIUnlocked'),
            actionContent: i18n.translate('AdminHTML.Synonym.Actions.goToAISynonym')
          }
        });
        return;
      }

      const localStorageKey = `${getShop()}-suggest-ai-synonym-click`;
      let allowSuggest = false;
      let suggestedAISynonym = [];
      let suggestedIdx = -1;

      // validate to allow click suggest synonym by AI
      const suggestedAISynonymStr = localStorage.getItem(localStorageKey);
      if (suggestedAISynonymStr && !_.isEmpty(suggestedAISynonymStr)) {
        suggestedAISynonym = JSON.parse(suggestedAISynonymStr);

        suggestedIdx = suggestedAISynonym.findIndex(item => item.index === index);
        // If click in same row less than 2 times => allow ai suggest
        if (suggestedIdx > -1 && suggestedAISynonym[suggestedIdx].searchTerms.length < 2) {
          allowSuggest = true;
        }

        // If click in new row, check number of clicked row less than 2 times => allow ai suggest
        if (suggestedIdx === -1 && suggestedAISynonym.length < 2) {
          allowSuggest = true;
        }
      } else {
        // If not clicked before => allow ai suggest
        allowSuggest = true;
      }

      // if allow ai suggest => suggest data and save to local storage
      if (allowSuggest) {
        let { synonyms = [] } = formValues;
        let suggestedTerms = [];
        for (const suggested of suggestedAISynonym) {
          suggestedTerms = suggestedTerms.concat(suggested.searchTerms);
        }

        let suggestSearchTerm = aiSynonyms[0].searchTerm;
        let suggestSynonym = aiSynonyms[0].synonyms;

        for (const synonym of aiSynonyms) {
          const checkSuggestedIdx = suggestedTerms.findIndex(item => item === synonym.searchTerm);

          if (checkSuggestedIdx === -1) {
            suggestSearchTerm = synonym.searchTerm;
            suggestSynonym = synonym.synonyms;
            break;
          }
        }

        // update data
        let synonymsClone = _.cloneDeep(synonyms);
        synonymsClone[index].biDirection = '=>';
        synonymsClone[index].searchTerm = suggestSearchTerm;
        synonymsClone[index].synonyms = suggestSynonym;

        updateField('synonyms', synonymsClone);

        // save to local storage
        if (suggestedIdx === -1) {
          suggestedAISynonym.push({ index, searchTerms: [suggestSearchTerm] });
        } else {
          suggestedAISynonym[suggestedIdx].searchTerms.push(suggestSearchTerm);
        }

        localStorage.setItem(localStorageKey, JSON.stringify(suggestedAISynonym));
      }
      // If not allow to suggest => show toast
      else {
        this.setState({
          toastOptions: {
            message: i18n.translate('AdminHTML.Synonym.Validate.invalid_Synonyms_AILimitFree'),
            actionContent: i18n.translate('AdminHTML.Common.Action.unLock')
          }
        });
      }
    } catch (error) {}
  };

  // Save synonyms
  handleDefaultSubmit = e => {
    e.preventDefault();
  };

  async handleSave(data) {
    const { i18n, resetForm, markDoneStep } = this.props;
    const { synonyms = [] } = data;

    // Get synonym having term
    let validSynonyms = _.filter(synonyms, synonym => {
      return !_.isEmpty(synonym.searchTerm) && !_.isEmpty(synonym.synonyms);
    });

    const validateSynonymsResult = checkValidateSynonyms(validSynonyms);
    if (!_.isEmpty(validateSynonymsResult)) {
      this.setState({
        dismissedBanner: false,
        messageError: i18n.translate(validateSynonymsResult)
      });
      return;
    }

    this.setState({ dismissedBanner: true, messageError: '' });

    // Save if having new terms
    // Build synonyms post package
    const saveSynonyms = buildSynonyms(validSynonyms);

    // Save to db
    const result = await this.props.bulkSynonyms(saveSynonyms);

    const { type } = result;
    if (type === 'BULK_SYNONYMS_SUCCESS') {
      setTimeout(() => {
        resetForm();
        this.backToManagementPage();
      }, 500);
    }

    markDoneStep('V2_SEARCH1', {
      isDone: true
    });
  }

  // Handle hide banner
  handleDismissBanner = () => {
    // Hide banner
    this.setState({ dismissedBanner: true, messageError: '' });
  };

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { dirty, i18n } = this.props;
    return confirmLeavePage(event, dirty, i18n);
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Update redux-form field
  updateField: (field, value) => dispatch(change(`NewSynonymForm`, field, value)),
  bulkSynonyms: synonyms => dispatch(synonymActions.bulkSynonyms(synonyms, ownProps.i18n)),
  resetForm: () => dispatch(reset('NewSynonymForm')),
  markDoneStep: (stepName, value) =>
    dispatch(quickStartGuideAction.markDoneStep(stepName, value, ownProps.i18n)),
  getAISynonyms: (skip, limit) =>
    dispatch(synonymActions.getSynonyms({ skip, limit }, ownProps.i18n, SynonymType.AI))
});

const mapStateToProps = (state, props) => {
  const formValues = getFormValues('NewSynonymForm')(state);
  const aiSynonyms = _.get(state.synonym, 'aiSynonyms', []);
  const synonyms = [...Array(3).keys()].map((item, key) => {
    return {
      searchTerm: '',
      biDirection: '<=>',
      synonyms: '',
      allowSuggestion: false
    };
  });

  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const chargeInfoActive = _.get(tenantApp, 'chargeInfoActive') || {};
  const enableAISearch = _.get(tenantApp, 'settings.instantSearch.settings.AI-synonyms', false);
  const statusStore = _.get(state.oauth, 'subscription.status', '');
  const addonStatus = _.get(state.subscriptionV3, `addonStatus.${AI_POWERED_SEARCH_NAME}`, {});
  const aiSearchStatus = getAISearchStatus(enableAISearch, statusStore, addonStatus);

  return {
    formValues,
    initialValues: {
      synonyms
    },
    saving: _.get(state.synonym, 'saving', false),
    success: _.get(state.synonym, 'success', false),
    aiSynonyms,
    isSubscribedApp: !_.isEmpty(chargeInfoActive),
    enableAISearch,
    enabledSubscriptionV4: _.get(
      state.oauth.tenantApp,
      'settings.subscriptionV4.settings.enable',
      false
    ),
    aiSearchStatus
  };
};

const NewSynonymForm = reduxForm({
  form: 'NewSynonymForm', // a unique identifier for this form
  enableReinitialize: true
})(NewSynonym);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(NewSynonymForm));
